/**
 * NeuralWaveform
 * ---------------------------------------------------------------------------
 * Ambient multi-strand signal waveform rendered on a single 2D canvas.
 *
 * Model:
 *   envelope  - several slowly drifting, skewed, "breathing" Gaussian energy
 *               bumps decide WHERE the wave is tall at any instant. Tapered to
 *               zero at the left/right edges so the composition always reads
 *               calm at the frame border and busy inside it.
 *   carrier   - four detuned harmonics (irrational frequency ratios) supply the
 *               fine wiggle. A slow FM drift keeps the pattern from ever
 *               visibly repeating.
 *   bundle    - the line is stroked N times. Each trace lags in time, shears in
 *               phase and scales the LOCAL amplitude non-linearly, so strands
 *               collapse into one bright line where the signal is calm and fan
 *               into a woven tangle where it is energetic.
 *   blending  - additive ('lighter'), so overlapping strands sum into a hot,
 *               near-white core at crossings instead of stacking flat opacity.
 *
 * No dependencies. Framework agnostic. Drive it from a useEffect and call
 * destroy() on cleanup.
 */

const TAU = Math.PI * 2;

/** Deterministic per-index pseudo-random in [0,1). Same shape every reload. */
function hash(i, seed) {
  const x = Math.sin(i * 127.1 + seed * 311.7) * 43758.5453;
  return x - Math.floor(x);
}

/** Hermite smoothstep, clamped. */
function smoothstep(edge0, edge1, x) {
  const t = Math.min(1, Math.max(0, (x - edge0) / (edge1 - edge0)));
  return t * t * (3 - 2 * t);
}

const DEFAULTS = {
  // Gradient the strands are stroked with, left -> right across the canvas.
  // Matches the site's teal -> indigo brand ramp.
  stops: [
    [0.0, '43,179,163'],
    [0.34, '58,163,214'],
    [0.7, '104,132,238'],
    [1.0, '99,102,241'],
  ],
  traceCount: 16, // strands in the bundle
  baseOpacity: 0.42, // opacity of the brightest strand
  bloomStrength: 0.13, // wide soft pass, relative to the crisp pass
  bloomWidth: 5.5, // px
  coreWidth: 1.15, // px
  lagStep: 0.038, // seconds of time offset between adjacent strands
  shearStep: 0.85, // carrier phase shear across the bundle
  fanCurve: 1.35, // >1 keeps inner strands tight, throws outer ones wide
  fanSpread: 0.3, // max +/- amplitude scaling at the edge of the bundle
  pointStep: 5, // px between sample points (curve-smoothed afterwards)
  ambient: 0.014, // minimum wiggle in "calm" zones, as a fraction of height
  edgeFade: 0.16, // fraction of width that tapers to flat at each edge
  // Soft ceiling on the half-spread of the OUTERMOST strand, as a fraction of
  // height. The fan scaling is divided out of it in _measure, so widening the
  // bundle can never push the outer strands into the canvas edge.
  maxAmplitude: 0.42,
  speedMultiplier: 0.85,
  fadeInSeconds: 0.9,
  maxDpr: 2,
};

// Drifting energy bumps. `speed` is in canvas-widths per second against the
// wrapped `range`, so a full traverse takes 1.6 / speed seconds (~80-260s).
const BUMPS = [
  { speed: 0.0135, phase: 0.1, width: 0.15, amp: 0.3, skew: 0.6, breath: 0.13, breathRate: 0.21, range: 1.6 },
  { speed: -0.0092, phase: 0.62, width: 0.115, amp: 0.24, skew: -0.4, breath: 0.17, breathRate: 0.29, range: 1.6 },
  { speed: 0.0205, phase: 0.86, width: 0.085, amp: 0.17, skew: 0.9, breath: 0.22, breathRate: 0.37, range: 1.6 },
  { speed: -0.0061, phase: 0.35, width: 0.205, amp: 0.13, skew: 0.0, breath: 0.11, breathRate: 0.17, range: 1.6 },
];

// Detuned harmonics: ratios are deliberately not integers so the sum has no
// short common period. [multiple, weight, timeRate, phase]
const HARMONICS = [
  [3.0, 1.0, 0.31, 0.0],
  [6.83, 0.52, -0.23, 1.7],
  [13.37, 0.29, 0.55, 0.4],
  [23.71, 0.13, -0.79, 2.2],
];
const CARRIER_NORM = 1 / HARMONICS.reduce((s, h) => s + h[1], 0);

export class NeuralWaveform {
  constructor(canvas, options = {}) {
    this.canvas = canvas;
    this.ctx = canvas.getContext('2d');
    this.config = { ...DEFAULTS, ...options };

    this.width = 0;
    this.height = 0;
    // Seed forward so the very first painted frame is already interesting
    // rather than a symmetric, dead-flat line.
    this.time = 12.5;
    this.running = false;

    this._rafId = null;
    this._lastFrameAt = 0;
    this._elapsed = 0;
    this._visible = true;
    this._gradient = null;
    this._painted = false;
    this._env = null;
    this._ys = null;
    this._top = null;
    this._sampleCount = 0;

    this._builtCount = 0; // _measure builds the bundle for the current breakpoint

    this._onResize = this._onResize.bind(this);
    this._onVisibilityChange = this._onVisibilityChange.bind(this);
    this._onReducedMotionChange = this._onReducedMotionChange.bind(this);
    this._tick = this._tick.bind(this);

    this._reducedMotionMQ = window.matchMedia('(prefers-reduced-motion: reduce)');

    this._resizeObserver = new ResizeObserver(this._onResize);
    this._resizeObserver.observe(canvas.parentElement || canvas);

    // Start painting slightly before the panel scrolls into view so it is
    // never caught mid fade-in.
    this._intersectionObserver = new IntersectionObserver(
      (entries) => { this._visible = entries[0]?.isIntersecting ?? true; },
      { threshold: 0, rootMargin: '120px' }
    );
    this._intersectionObserver.observe(canvas);

    document.addEventListener('visibilitychange', this._onVisibilityChange);
    this._reducedMotionMQ.addEventListener('change', this._onReducedMotionChange);

    this._measure();
  }

  // ── setup ────────────────────────────────────────────────────────────────

  /**
   * Build the strand bundle for `n` traces.
   *
   * Rebuilt (rather than sliced) whenever the breakpoint changes the count:
   * the traces are laid out symmetrically from u=-1 to u=+1, so taking the
   * first N of a wider bundle would give a lopsided fan with no lower strands.
   */
  _buildTraces(n) {
    const { fanCurve, fanSpread, lagStep, shearStep } = this.config;
    this._builtCount = n;
    this.traces = [];
    for (let i = 0; i < n; i++) {
      // u runs -1 .. 1 across the bundle; 0 is the centre strand.
      const u = n === 1 ? 0 : (i - (n - 1) / 2) / ((n - 1) / 2);
      const curved = Math.sign(u) * Math.pow(Math.abs(u), fanCurve);
      this.traces.push({
        // Non-linear amplitude fan plus a small deterministic jitter, so the
        // strands weave instead of sitting in a perfectly even accordion.
        ampScale: 1 + curved * fanSpread + (hash(i, 3) - 0.5) * 0.035,
        phaseShear: u * shearStep + (hash(i, 7) - 0.5) * 0.22,
        lag: u * lagStep * ((n - 1) / 2),
        // Gaussian falloff: hot core, softly fading fringe.
        weight: Math.min(1, Math.exp(-(u * u) * 1.9) * (0.82 + 0.36 * hash(i, 11))),
      });
    }
    // Brightest strands carry the bloom pass; the fringe does not need it.
    this._bloomCutoff = 0.5;
    // Widest strand in the bundle — the envelope ceiling is scaled by this so
    // the outermost trace, not the centre one, is what respects maxAmplitude.
    this._maxAmpScale = this.traces.reduce((m, tr) => Math.max(m, Math.abs(tr.ampScale)), 1);
  }

  _measure() {
    const parent = this.canvas.parentElement || this.canvas;
    const rect = parent.getBoundingClientRect();
    const dpr = Math.min(window.devicePixelRatio || 1, this.config.maxDpr);

    this.width = Math.max(1, Math.round(rect.width));
    this.height = Math.max(1, Math.round(rect.height));
    this.canvas.width = Math.round(this.width * dpr);
    this.canvas.height = Math.round(this.height * dpr);
    this.ctx.setTransform(dpr, 0, 0, dpr, 0, 0);

    const compact = this.width < 640;
    this._pointStep = compact ? this.config.pointStep * 1.8 : this.config.pointStep;
    const wanted = compact
      ? Math.max(8, Math.round(this.config.traceCount * 0.6))
      : this.config.traceCount;
    if (wanted !== this._builtCount) this._buildTraces(wanted);

    this._sampleCount = Math.ceil(this.width / this._pointStep) + 1;
    this._env = new Float32Array(this._sampleCount);
    this._ys = new Float32Array(this._sampleCount);
    // Topmost PAINTED y per sample column, refreshed every frame. Anything
    // anchored to the wave reads this rather than the envelope: the envelope
    // is the amplitude ceiling the carrier swings inside, so a marker placed
    // on it hovers above the strands instead of touching them.
    this._top = new Float32Array(this._sampleCount);
    this._xs = new Float32Array(this._sampleCount);
    this._taper = new Float32Array(this._sampleCount);

    const fade = this.config.edgeFade;
    for (let i = 0; i < this._sampleCount; i++) {
      const x = Math.min(i * this._pointStep, this.width);
      const u = x / this.width;
      this._xs[i] = x;
      // Raised to a power so the ramp leaves the edge slowly — the first few
      // percent of the frame stay visibly flat rather than lifting immediately.
      const w = smoothstep(0, fade, u) * smoothstep(0, fade, 1 - u);
      this._taper[i] = Math.pow(w, 1.6);
    }

    this._ceiling = (this.config.maxAmplitude * this.height) / this._maxAmpScale;
    this._buildGradient();
  }

  _buildGradient() {
    // One gradient object, reused for every strand and every frame. Per-strand
    // opacity is applied with globalAlpha instead of rebuilding the gradient.
    const g = this.ctx.createLinearGradient(0, 0, this.width, 0);
    for (const [at, rgb] of this.config.stops) g.addColorStop(at, `rgb(${rgb})`);
    this._gradient = g;
  }

  // ── sampling (for anchored UI over the wave) ─────────────────────────────

  /**
   * Find the visual crest of the wave inside a horizontal lane.
   *
   * Reads the geometry actually stroked last frame (`_top`), so the returned
   * point lies ON the topmost strand rather than on the envelope that bounds
   * it. Anchoring to the envelope leaves a gap wherever the carrier is not at
   * full swing — which is most of the time — and that gap is what makes a
   * marker look like it floats above the wave.
   *
   * This is safe to pin to because the carrier drifts slowly: its harmonics
   * advance at 0.23-0.79 rad/s, so the crest moves over seconds, not frames.
   *
   * Coordinates are CSS pixels relative to the canvas box, so they can be
   * written straight to an overlay positioned over the canvas.
   *
   * @param {number} loFrac  lane start, 0..1 of canvas width
   * @param {number} hiFrac  lane end, 0..1 of canvas width
   * @returns {{x:number, y:number, energy:number}|null} null before first paint
   */
  sampleLane(loFrac, hiFrac) {
    // _measure allocates a zeroed buffer, so "allocated" is not "meaningful" —
    // wait for a real frame or callers snap in from a flat baseline.
    if (!this._painted || this._sampleCount < 2 || !this._ceiling) return null;

    const last = this._sampleCount - 1;
    const lo = Math.max(0, Math.min(last, Math.floor(loFrac * last)));
    const hi = Math.max(lo, Math.min(last, Math.ceil(hiFrac * last)));

    let bi = lo;
    let bestY = Infinity;
    for (let i = lo; i <= hi; i++) {
      if (this._top[i] < bestY) { bestY = this._top[i]; bi = i; }
    }
    if (!Number.isFinite(bestY)) return null;

    const baseline = this.height * 0.5;
    // Half-height the outermost strand is allowed to reach. Peak height is
    // reported against it so lanes are directly comparable: a taller crest
    // always yields a larger number, whichever lane it is in.
    const span = this._ceiling * this._maxAmpScale;

    return {
      x: this._xs[bi],
      y: bestY,
      // 0 = flat, 1 = at the amplitude ceiling.
      energy: Math.max(0, Math.min(1, (baseline - bestY) / span)),
    };
  }

  /**
   * Height of the painted bundle's top edge at an arbitrary x, interpolated
   * between sample columns.
   *
   * Callers that ease a marker horizontally must read y through this rather
   * than easing the y from sampleLane: two independently smoothed coordinates
   * describe a point that is on neither the old crest nor the new one, which
   * is what lifts a marker off the wave. Resolving y from x keeps it on the
   * curve by construction, at any width and through any resize.
   *
   * @param {number} x  CSS pixels from the left edge of the canvas
   * @returns {number|null} y in CSS pixels, or null before first paint
   */
  topAt(x) {
    if (!this._painted || this._sampleCount < 2) return null;

    const last = this._sampleCount - 1;
    const f = Math.max(0, Math.min(last, x / this._pointStep));
    const i = Math.min(last - 1, Math.floor(f));
    const a = this._top[i];
    const b = this._top[i + 1];
    if (!Number.isFinite(a) || !Number.isFinite(b)) return null;

    return a + (b - a) * (f - i);
  }

  // ── signal ───────────────────────────────────────────────────────────────

  /** Fill this._env with the amplitude profile for the current instant. */
  _computeEnvelope(t) {
    const { height: H, width: W } = this;
    const ceiling = this._ceiling;
    const ambient = this.config.ambient * H;

    for (let i = 0; i < this._sampleCount; i++) this._env[i] = 0;

    for (const b of BUMPS) {
      // Wrap the drifting centre through `range` so bumps enter and leave the
      // frame instead of bouncing at the edges.
      let cf = (b.phase + t * b.speed) % b.range;
      if (cf < 0) cf += b.range;
      cf -= (b.range - 1) / 2;

      const cx = cf * W;
      const w = b.width * W;
      // Slow amplitude breathing keeps repeat passes of the same bump from
      // looking like the same peak twice.
      const amp = b.amp * H * (1 + b.breath * Math.sin(t * b.breathRate + b.phase * 7));
      const lo = 1 - b.skew * 0.35;
      const hi = 1 + b.skew * 0.35;

      for (let i = 0; i < this._sampleCount; i++) {
        const d = (this._xs[i] - cx) / w;
        const dd = d / (d < 0 ? lo : hi); // skew: asymmetric falloff
        this._env[i] += amp * Math.exp(-(dd * dd));
      }
    }

    for (let i = 0; i < this._sampleCount; i++) {
      const taper = this._taper[i];
      // Soft ceiling: when several bumps align the sum compresses smoothly
      // instead of clipping against the top and bottom of the canvas.
      const e = ceiling * Math.tanh((this._env[i] * taper) / ceiling);
      this._env[i] = e + ambient * (0.15 + 0.85 * taper);
    }
  }

  _carrier(xk, t, shear, fm) {
    let sum = 0;
    for (let h = 0; h < HARMONICS.length; h++) {
      const [mult, weight, rate, phase] = HARMONICS[h];
      sum += Math.sin(xk * mult + t * rate + phase + shear * (1 + h * 0.7) + fm) * weight;
    }
    return sum * CARRIER_NORM;
  }

  // ── render ───────────────────────────────────────────────────────────────

  _drawFrame() {
    const { ctx, width: W, height: H, config } = this;
    if (W <= 0 || H <= 0) return;

    ctx.clearRect(0, 0, W, H);

    const t = this.time;
    const baseline = H * 0.5;
    const k = TAU / W;
    const fm = 0.6 * Math.sin(t * 0.11); // slow frequency drift
    const fadeIn = smoothstep(0, 1, Math.min(1, this._elapsed / config.fadeInSeconds));

    this._computeEnvelope(t);

    // Reset before the bundle is stroked; each trace lowers it where it paints.
    this._top.fill(Infinity);

    ctx.globalCompositeOperation = 'lighter';
    ctx.lineCap = 'round';
    ctx.lineJoin = 'round';
    ctx.strokeStyle = this._gradient;

    for (let i = 0; i < this.traces.length; i++) {
      const tr = this.traces[i];
      const alpha = config.baseOpacity * tr.weight * fadeIn;
      if (alpha < 0.008) continue;

      const tLocal = t + tr.lag;
      for (let s = 0; s < this._sampleCount; s++) {
        const x = this._xs[s];
        const y = baseline + this._env[s] * tr.ampScale * this._carrier(x * k, tLocal, tr.phaseShear, fm);
        this._ys[s] = y;
        if (y < this._top[s]) this._top[s] = y;
      }

      // Quadratic curve through segment midpoints: smooths out the polyline
      // faceting, which is what lets pointStep stay coarse (and cheap).
      ctx.beginPath();
      ctx.moveTo(this._xs[0], this._ys[0]);
      for (let s = 1; s < this._sampleCount - 1; s++) {
        const mx = (this._xs[s] + this._xs[s + 1]) * 0.5;
        const my = (this._ys[s] + this._ys[s + 1]) * 0.5;
        ctx.quadraticCurveTo(this._xs[s], this._ys[s], mx, my);
      }
      ctx.lineTo(this._xs[this._sampleCount - 1], this._ys[this._sampleCount - 1]);

      // Same path, stroked twice: a wide faint pass for the halo, then a crisp
      // hairline for the strand. Replaces shadowBlur — cheaper and sharper.
      if (tr.weight > this._bloomCutoff) {
        ctx.globalAlpha = alpha * config.bloomStrength;
        ctx.lineWidth = config.bloomWidth;
        ctx.stroke();
      }
      ctx.globalAlpha = alpha;
      ctx.lineWidth = config.coreWidth;
      ctx.stroke();
    }

    ctx.globalAlpha = 1;
    ctx.globalCompositeOperation = 'source-over';
    this._painted = true;
  }

  // ── loop ─────────────────────────────────────────────────────────────────

  _tick(now) {
    const dt = Math.min((now - this._lastFrameAt) / 1000, 1 / 30);
    this._lastFrameAt = now;
    this.time += dt * this.config.speedMultiplier;
    this._elapsed += dt;
    if (this._visible) this._drawFrame();
    this._rafId = requestAnimationFrame(this._tick);
  }

  _resumeLoop() {
    if (this._rafId != null) return;
    this._lastFrameAt = performance.now();
    this._rafId = requestAnimationFrame(this._tick);
  }

  _pauseLoop() {
    if (this._rafId != null) cancelAnimationFrame(this._rafId);
    this._rafId = null;
  }

  _onResize() {
    this._measure();
    if (this._rafId == null) this._drawFrame();
  }

  _onVisibilityChange() {
    if (document.hidden) this._pauseLoop();
    else if (this.running && !this._reducedMotionMQ.matches) this._resumeLoop();
  }

  _onReducedMotionChange() {
    if (this._reducedMotionMQ.matches) {
      this._pauseLoop();
      this._elapsed = this.config.fadeInSeconds;
      this._drawFrame();
    } else if (this.running) {
      this._resumeLoop();
    }
  }

  // ── public ───────────────────────────────────────────────────────────────

  start() {
    this.running = true;
    if (this._reducedMotionMQ.matches) {
      this._elapsed = this.config.fadeInSeconds; // no fade, just one still frame
      this._drawFrame();
      return;
    }
    this._resumeLoop();
  }

  stop() {
    this.running = false;
    this._pauseLoop();
  }

  destroy() {
    this.stop();
    this._resizeObserver.disconnect();
    this._intersectionObserver.disconnect();
    document.removeEventListener('visibilitychange', this._onVisibilityChange);
    this._reducedMotionMQ.removeEventListener('change', this._onReducedMotionChange);
  }
}

export default NeuralWaveform;
